import { Controller } from "@hotwired/stimulus"

// Animated highlight for the delivery-type pill group. A single solid pill
// (`.oc-pill-slider`) sits *behind* the buttons and slides under the active one;
// the active button's label simply recolours white (CSS `.oc-pill-button.active`).
// There is no cloned content inside the slider, so there is nothing that can fall
// out of alignment mid-transition — the previous clip-window + clone technique is
// what produced the brief "two labels" double during the slide.
export default class extends Controller {
  static targets = ["button", "slider"]

  connect() {
    this.windowWidth = window.innerWidth
    // Hide the slider until it has been positioned once (CSS gates on this class)
    // so it never flashes at its default left:0 on first paint.
    this.element.classList.remove("oc-pill-group--ready")

    // Wait a tick for layout to settle before the initial (un-animated) position.
    requestAnimationFrame(() => {
      this.updateSlider(false)
      requestAnimationFrame(() => {
        this.element.classList.add("oc-pill-group--ready")
      })
    })

    this.resizeHandler = this.handleResize.bind(this)
    window.addEventListener("resize", this.resizeHandler)

    // A webfont swapping in changes every pill's width but fires no resize event,
    // so the slider stayed measured against the fallback face. Same for a group
    // that was laid out inside a hidden modal when connect() ran.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.updateSlider(false)).catch(() => {})
    }

    if (typeof ResizeObserver !== "undefined") {
      this.observer = new ResizeObserver(() => this.updateSlider(false))
      this.observer.observe(this.element)
    }
  }

  disconnect() {
    window.removeEventListener("resize", this.resizeHandler)
    if (this.observer) this.observer.disconnect()
  }

  handleResize() {
    // Only re-measure on a real width change (ignore iOS URL-bar height jitter).
    if (window.innerWidth !== this.windowWidth) {
      this.windowWidth = window.innerWidth
      this.updateSlider(false)
    }
  }

  updateSlider(animate = true) {
    if (!this.hasSliderTarget) return

    const activeBtn = this.buttonTargets.find((b) => b.classList.contains("active"))
    if (!activeBtn) {
      this.sliderTarget.style.opacity = "0"
      return
    }

    this.sliderTarget.style.opacity = "1"
    this.sliderTarget.style.transition = animate
      ? "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      : "none"

    this.sliderTarget.style.transform = `translateX(${activeBtn.offsetLeft}px)`
    this.sliderTarget.style.width = `${activeBtn.offsetWidth}px`
  }

  slide(event) {
    // Optimistically reflect the selection before the ajax/reload finishes.
    if (event.currentTarget.classList.contains("disabled")) return

    this.buttonTargets.forEach((b) => b.classList.remove("active"))
    event.currentTarget.classList.add("active")
    this.updateSlider(true)
  }
}
